import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { Box, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { ComponentProps, ReactNode, useState } from "react";
import { requestMeetingCoordination } from "../../../api/invitations";
import { useAppDispatch } from "../../../store";
import { AppButton } from "./index";

const popperMotion = keyframes`
  0% {
    opacity: 0;
    transform: translateY(6px) scale(0.97);
  }
  70% {
    opacity: 1;
    transform: translateY(-1px) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const AnimatedTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    enterTouchDelay={0}
    leaveTouchDelay={2000}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1, 1.5),
    fontSize: theme.typography.pxToRem(12),
    maxWidth: 220,
    animation: `${popperMotion} 220ms ${theme.transitions.easing.easeOut}`,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
  },
}));

type RequestCoordinationButtonProps = {
  invitationId?: string | number;
  initiatedBy?: "vendor" | "ambassador" | "client" | "delegate";
  tooltipPlacement?: TooltipProps["placement"];
  onSuccess?: (response: any) => void;
  renderIcon?: ReactNode;
} & Omit<ComponentProps<typeof AppButton>, "children" | "onClick">;

export function RequestCoordinationButton({
  invitationId,
  initiatedBy = "vendor",
  tooltipPlacement = "top",
  onSuccess,
  renderIcon,
  color = "text.secondary",
  variant = "text",
  size = "small",
  disabled,
  loading: loadingProp,
  ...buttonProps
}: RequestCoordinationButtonProps) {
  const dispatch = useAppDispatch();
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = internalLoading || Boolean(loadingProp);
  const isDisabled = disabled || !invitationId || isLoading;
  const icon = renderIcon ?? (
    <HelpOutlineOutlinedIcon
      fontSize="small"
      color="secondary"
      sx={{ color: "text.secondary" }}
    />
  );

  const handleClick = async () => {
    if (!invitationId || isDisabled) return;

    setInternalLoading(true);
    const response = await dispatch(
      requestMeetingCoordination({
        id: invitationId,
        initiatedBy,
      })
    );
    setInternalLoading(false);

    if (response?.success && typeof onSuccess === "function") {
      onSuccess(response);
    }
  };

  return (
    <AnimatedTooltip
      title="Click to have our team help finalize the meeting time."
      placement={tooltipPlacement}
    >
      <Box component="span" sx={{ visibility: "hidden" }}>
        <AppButton
          variant={variant}
          color={color}
          size={size}
          disabled={isDisabled}
          loading={isLoading}
          startIcon={icon}
          onClick={handleClick}
          {...buttonProps}
        >
          <Typography color="text.secondary" variant="body2" fontWeight={600}>
            Help
          </Typography>
        </AppButton>
      </Box>
    </AnimatedTooltip>
  );
}
